import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import { jwtDecode } from "jwt-decode";
import { supabase } from "../services/supabaseClient";
import { useAuth } from "./AuthContext";
import { ChapterPlan } from "../types/membership";

interface UserContextMemberType {
  chapters: {
    organization_id: number;
    chapter_id: number;
    dba: string|null;
    mygreek_enabled: boolean;
    disabled_message: string|null;
    state: string;
    country: string;
    plan: ChapterPlan;
    plan_expires_at: string | null;
    crowded_application_status?: string | null;
  };
  member_id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  onboarding_finalized: boolean;
  email_validated: boolean | null;
  balance: number;
  credit_balance?: number;
  public_id: string|null;
  payment_plan_status: string;
}

interface Membership {
  member_id: number;
  chapter_id: number;
  chapter_name: string;
}

// MYG-116: accountants toggle between the firm overview and a chapter workspace. The
// mode is persisted so it survives switchChapter's full reload and manual refreshes.
export type AccountantViewMode = "overview" | "chapter";
const ACCOUNTANT_VIEW_MODE_KEY = "mygreek:accountantViewMode";

interface UserContextType {
  member: UserContextMemberType | null;
  memberships: Membership[];
  loading: boolean;
  switching: boolean;
  // MYG-116: this login is a provisioned accountant (row in api.accountants_users).
  isAccountant: boolean;
  // True until the accountant lookup resolves, so "/" doesn't flash the wrong landing.
  accountantLoading: boolean;
  accountantFirmId: number | null;
  accountantFirmName: string | null;
  // The accountant's own first name (from api.accountants_users, not their member row).
  accountantFirstName: string | null;
  // MYG-116: which accountant view "/" renders ('overview' table vs the chapter dashboard).
  accountantViewMode: AccountantViewMode;
  setAccountantViewMode: (mode: AccountantViewMode) => void;
  refreshMember: () => Promise<void>;
  switchChapter: (memberId: number, redirectTo?: string) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const MemberProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [member, setMember] = useState<UserContextMemberType | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [isAccountant, setIsAccountant] = useState(false);
  const [accountantLoading, setAccountantLoading] = useState(true);
  const [accountantFirmId, setAccountantFirmId] = useState<number | null>(null);
  const [accountantFirmName, setAccountantFirmName] = useState<string | null>(null);
  const [accountantFirstName, setAccountantFirstName] = useState<string | null>(null);
  const [accountantViewMode, setAccountantViewModeState] = useState<AccountantViewMode>(
    () => (localStorage.getItem(ACCOUNTANT_VIEW_MODE_KEY) === "chapter" ? "chapter" : "overview")
  );
  const { session } = useAuth();

  // Persist synchronously so the value survives switchChapter's window.location reload.
  const setAccountantViewMode = (mode: AccountantViewMode) => {
    localStorage.setItem(ACCOUNTANT_VIEW_MODE_KEY, mode);
    setAccountantViewModeState(mode);
  };

  // The active membership is whatever the JWT claim points at (set by the hook from
  // app_metadata.active_member_id). Deriving it from the token means a switchChapter →
  // refreshSession re-runs the loader below with the new active member_id.
  const activeMemberId = useMemo(() => {
    if (!session?.access_token) return null;
    try {
      // The hook stamps member_id as a JSON string, so coerce to a number to compare
      // against members.member_id.
      const claim = jwtDecode<{ member_id?: string | number }>(session.access_token).member_id;
      return claim != null ? Number(claim) : null;
    } catch {
      return null;
    }
  }, [session?.access_token]);

  const fetchMemberData = async (
    userId: string,
    activeId: number | null,
    shouldSetLoading = true,
    // Lets a caller abandon a stale in-flight fetch (e.g. the effect re-fired) so its
    // setters don't clobber a newer result.
    isStale: () => boolean = () => false
  ) => {
    if (shouldSetLoading && !isStale()) {
      setLoading(true);
    }
    const chapterFields = `organization_id, chapter_id, dba, mygreek_enabled, disabled_message, country, state, plan, plan_expires_at${!import.meta.env.PROD ? ', crowded_application_status' : ''}`;
    // Select every membership row for this auth user (allowed by the "Select own
    // memberships by auth" RLS policy); pick the active one by the JWT claim.
    const { data, error } = await supabase
      .from("members")
      .select(
        `chapter_id, chapters:chapter_id (${chapterFields}), member_id, first_name, last_name, email, phone, onboarding_finalized, email_validated, balance, credit_balance, public_id, payment_plan_status, sent_to_collections_at`
      )
      .eq("auth_id", userId);

    if (error) {
      console.warn(error);
      if (shouldSetLoading && !isStale()) {
        setLoading(false);
      }
      return;
    }

    if (data && !isStale()) {
      // Safe cast: `chapters:chapter_id(...)` is a one-to-one FK join (single object, not an
      // array); the Supabase-generated types don't model the embed alias, hence the double cast.
      const rows = data as unknown as (UserContextMemberType & { chapter_id: number })[];
      // chapter_id is read from the members column (always present) rather than the
      // chapters embed, which is null for any chapter the user isn't currently active in.
      setMemberships(
        rows.map((row) => ({
          member_id: row.member_id,
          chapter_id: row.chapter_id,
          chapter_name: row.chapters?.dba ?? `Chapter ${row.chapter_id}`,
        }))
      );
      // The active row (matching the JWT claim) is the one whose chapters embed resolves.
      // Fall back to any row with a resolvable chapter so member.chapters is never null.
      const active =
        rows.find((row) => row.member_id === activeId) ??
        rows.find((row) => !!row.chapters) ??
        rows[0] ??
        null;
      setMember(active);

      if (shouldSetLoading) {
        setLoading(false);
      }
    }
  };

  const refreshMember = async () => {
    if (session?.user?.id) {
      await fetchMemberData(session.user.id, activeMemberId, false);
    }
  };

  // Switch which chapter the user is acting in: the edge function records the choice on
  // app_metadata, refreshSession mints a JWT with the new active membership, then a full
  // reload re-inits every chapter-scoped screen against the new token.
  const switchChapter = async (memberId: number, redirectTo = "/") => {
    setSwitching(true);
    const { error } = await supabase.functions.invoke("switch-chapter", {
      body: { member_id: memberId },
    });
    if (error) {
      setSwitching(false);
      throw error;
    }
    // If the refresh fails we'd reload on the OLD token (wrong chapter) — surface it instead.
    const { error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) {
      setSwitching(false);
      throw refreshError;
    }
    // Full reload re-inits every chapter-scoped screen against the new token. Accountants
    // (MYG-116) set accountantViewMode='chapter' before this, so the "/" reload lands on
    // the chapter dashboard; redirectTo stays "/" for everyone by default.
    window.location.assign(redirectTo);
  };

  useEffect(() => {
    let ignore = false;
    async function getProfile() {
      if (!session?.user?.id) {
        if (!ignore) {
          setLoading(false);
          setMember(null);
          setMemberships([]);
        }
        return;
      }

      await fetchMemberData(session.user.id, activeMemberId, true, () => ignore);
    }

    getProfile();

    return () => {
      ignore = true;
    };
  }, [session?.user?.id, activeMemberId]); // Re-derive active membership when the claim changes

  // MYG-116: detect accountant identity, independent of the active chapter. RLS on
  // api.accountants_users returns only the caller's own row, so a hit means this login
  // is a provisioned accountant. Runs alongside the member fetch (small, fast query).
  useEffect(() => {
    let ignore = false;
    async function checkAccountant() {
      if (!session?.user?.id) {
        if (!ignore) {
          setIsAccountant(false);
          setAccountantFirmId(null);
          setAccountantFirmName(null);
          setAccountantFirstName(null);
          setAccountantLoading(false);
        }
        return;
      }
      const { data, error } = await supabase
        .from("accountants_users")
        .select("id, firm_id, first_name, accountant_firms(name)")
        .eq("auth_id", session.user.id)
        .maybeSingle();
      if (!ignore) {
        if (error) {
          console.warn(error);
          setAccountantLoading(false);
          return;
        }
        const row = data as
          | {
              firm_id?: number;
              first_name?: string | null;
              accountant_firms?: { name?: string } | { name?: string }[] | null;
            }
          | null;
        const firm = Array.isArray(row?.accountant_firms)
          ? row?.accountant_firms[0]
          : row?.accountant_firms;
        setIsAccountant(!!data);
        setAccountantFirmId(row?.firm_id ?? null);
        setAccountantFirmName(firm?.name ?? null);
        setAccountantFirstName(row?.first_name ?? null);
        setAccountantLoading(false);
      }
    }
    checkAccountant();
    return () => {
      ignore = true;
    };
  }, [session?.user?.id]);

  return (
    <UserContext.Provider
      value={{
        member,
        memberships,
        loading,
        switching,
        isAccountant,
        accountantLoading,
        accountantFirmId,
        accountantFirmName,
        accountantFirstName,
        accountantViewMode,
        setAccountantViewMode,
        refreshMember,
        switchChapter,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useMember = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useMember must be used within a MemberProvider");
  }
  return context;
};
